import React, { useContext } from "react";
import { Link } from "gatsby";
import Img from "gatsby-image";
import PropTypes from "prop-types";
import { AppContext } from "~context/AppContext";
import { DocumentContext } from "~context/DocumentContext";
import AppearOnScroll from "~components/AppearOnScroll";
import Button from "~components/Button";
import StickerNew from "~components/svg/StickerNew";
import StickerSave from "~components/svg/StickerSave";
import StickerSoldOut from "~components/svg/StickerSoldOut";

const ProductGrid = ({ categorised, excludedId, max, open, products }) => {
  const { activeCategories, addToCart, inventoryCache } = useContext(
    AppContext
  );
  const { device } = useContext(DocumentContext);

  //

  let renderedProducts = JSON.parse(JSON.stringify(products));

  if (max) {
    renderedProducts = products.slice(0, max);
  }

  if (excludedId) {
    let excludedIndex = null;

    renderedProducts.forEach((product, productIndex) => {
      if (excludedIndex) {
        return;
      }

      if (product.id === excludedId) {
        excludedIndex = productIndex;
      }
    });

    if (excludedIndex !== null) {
      renderedProducts.splice(excludedIndex, 1);
    }
  }

  //

  return (
    <section className="product-grid w-full relative pt-12 pb-12">
      {renderedProducts?.[0] && (
        <ul className="grid">
          {renderedProducts.map((product, productIndex) => {
            let image = product.images[0].originalSrc;

            if (product?.frontmatter?.image?.childImageSharp) {
              image = product.frontmatter.image.childImageSharp;
            }

            let itemVisible = true;

            if (categorised) {
              itemVisible =
                activeCategories?.[0] && activeCategories[0] === `All`;

              if (!itemVisible && product?.frontmatter?.categories?.[0]) {
                product.frontmatter.categories.forEach(productCategory => {
                  if (itemVisible) {
                    return;
                  }

                  itemVisible = activeCategories.includes(productCategory);
                });
              }
            }

            if (!itemVisible) {
              return <></>;
            }

            //

            let stocked = true;

            if (inventoryCache?.[product?.variants?.[0]?.id] <= 0) {
              stocked = false;
            }

            let stickerJSX;

            if (!stocked) {
              stickerJSX = (
                <StickerSoldOut className="product-grid__icon w-full h-full relative block" />
              );
            } else if (product?.frontmatter?.new === true) {
              stickerJSX = (
                <StickerNew className="product-grid__icon w-full h-full relative block" />
              );
            } else if (product?.frontmatter?.save === true) {
              stickerJSX = (
                <StickerSave className="product-grid__icon w-full h-full relative block" />
              );
            }

            return (
              <li
                key={product.handle}
                className="product-grid__item animation-appear-slow grid-end-12 sm:grid-end-6 relative mb-10 md:mb-24 md:px-4"
                style={{ animationDelay: `${productIndex * 50}ms` }}
              >
                <AppearOnScroll className="h-full relative flex flex-col items-stretch">
                  {({ visible }) => (
                    <>
                      <div className="w-32 h-32 absolute top-0 right-0 z-20 mr-4 mt-4">
                        {visible && stickerJSX}
                      </div>

                      <Link to={product.slug}>
                        <div className="product-grid__item__image square overflow-hidden">
                          {(image?.fluid && (
                            <figure className="w-full h-full absolute top-0 right-0 bottom-0 left-0">
                              <Img
                                className="w-full h-full relative block object-cover"
                                fluid={image.fluid}
                                alt={product.frontmatter.title}
                              />
                            </figure>
                          )) || (
                            <figure className="w-full h-full absolute top-0 right-0 bottom-0 left-0">
                              <img
                                className="w-full h-full relative block object-cover"
                                src={product.images[0].originalSrc}
                                alt={product.handle}
                              />
                            </figure>
                          )}
                        </div>
                      </Link>

                      <article
                        className="product-grid__item__text relative flex flex-col items-center justify-between text-center"
                        style={{ flex: `1 0 auto` }}
                      >
                        <Link
                          to={product.slug}
                          className="relative pb-8 sm:pb-6"
                        >
                          <h2
                            className={`product-grid__item__title mt-6 md:mt-12 ${
                              device === `desktop` ? `f3` : `f4`
                            }`}
                          >
                            {product.frontmatter.title}
                          </h2>

                          <p className="desktop-only mt-6 b1">
                            {product.frontmatter.description}
                          </p>

                          <h3
                            className={`mt-5 sm:mt-3 md:mt-5 ${
                              device === `desktop` ? `b1` : `f5`
                            }`}
                          >
                            {product.variants[0].price}
                          </h3>
                        </Link>

                        <Button
                          className={`transition-opacity ${
                            inventoryCache
                              ? ``
                              : `opacity-0 pointer-events-none`
                          } ${
                            open && stocked ? `` : `pointer-events-none`
                          } w-full pt-4 pb-4`}
                          color={`${open && stocked ? `orange` : `cream`}`}
                          onClick={() => {
                            if (!open || !stocked) {
                              return;
                            }

                            const productWithVariant = JSON.parse(
                              JSON.stringify(product)
                            );

                            [productWithVariant.variant] = product.variants;

                            addToCart(productWithVariant);
                          }}
                        >
                          <span className="f-button">
                            {open && stocked ? `Add to cart` : `Sold out`}
                          </span>
                        </Button>
                      </article>
                    </>
                  )}
                </AppearOnScroll>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

ProductGrid.defaultProps = {
  categorised: false,
  excludedId: null,
  max: null
};
ProductGrid.propTypes = {
  categorised: PropTypes.bool,
  excludedId: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  max: PropTypes.number
};

export default ProductGrid;
